import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import AdminAPI from '../../utils/adminApi';

const AdminSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await AdminAPI.get('/api/manage/settings');
      setSettings(data.settings);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      setMessage('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, newValue) => {
    try {
      setSaving(true);
      setMessage('');
      
      await AdminAPI.put(`/api/manage/settings/${key}`, {
        key: key,
        value: newValue
      });
      
      // Update local state
      setSettings(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          value: newValue
        }
      }));
      
      setMessage('Setting updated successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Failed to update setting:', err);
      setMessage('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const toggleBooleanSetting = (key) => {
    const currentValue = settings[key]?.value === 'true';
    const newValue = (!currentValue).toString();
    updateSetting(key, newValue);
  };

  const renderBooleanSetting = (key, title, description) => {
    const setting = settings[key];
    if (!setting) return null;
    
    const isEnabled = setting.value === 'true';
    
    return (
      <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
            <p className="text-gray-400 text-sm">{description}</p>
          </div>
          <button
            onClick={() => toggleBooleanSetting(key)}
            disabled={saving}
            className={`ml-4 p-2 rounded-lg transition ${
              isEnabled 
                ? 'text-green-400 hover:text-green-300' 
                : 'text-gray-500 hover:text-gray-400'
            } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isEnabled ? <FaToggleOn size={32} /> : <FaToggleOff size={32} />}
          </button>
        </div>
        <div className="mt-2">
          <span className={`text-xs px-2 py-1 rounded ${
            isEnabled 
              ? 'bg-green-500/20 text-green-400' 
              : 'bg-red-500/20 text-red-400'
          }`}>
            {isEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>
    );
  };

  const renderTextSetting = (key, title, description) => {
    const setting = settings[key];
    if (!setting) return null;

    return (
      <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
        <p className="text-gray-400 text-sm mb-3">{description}</p>
        <div className="flex space-x-2">
          <input
            type="text"
            value={setting.value}
            onChange={(e) => setSettings(prev => ({
              ...prev,
              [key]: { ...prev[key], value: e.target.value }
            }))}
            className="flex-1 px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
          />
          <button
            onClick={() => updateSetting(key, setting.value)}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded flex items-center space-x-1"
          >
            <FaSave />
            <span>Save</span>
          </button>
        </div>
      </div>
    );
  };

  const renderNumberSetting = (key, title, description, unit = '', min = 0, max = null) => {
    const setting = settings[key];
    if (!setting) return null;

    return (
      <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
        <p className="text-gray-400 text-sm mb-3">{description}</p>
        <div className="flex space-x-2 items-center">
          <input
            type="number"
            value={setting.value}
            min={min}
            max={max}
            onChange={(e) => setSettings(prev => ({
              ...prev,
              [key]: { ...prev[key], value: e.target.value }
            }))}
            className="w-24 px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
          />
          {unit && <span className="text-gray-400">{unit}</span>}
          <button
            onClick={() => updateSetting(key, setting.value)}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded flex items-center space-x-1"
          >
            <FaSave />
            <span>Save</span>
          </button>
        </div>
        {setting.value === '0' && (
          <p className="text-yellow-400 text-xs mt-2">Polling is disabled when set to 0</p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-xl">Loading settings...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <button
              onClick={() => navigate('/manage')}
              className="flex items-center space-x-2 text-gray-400 hover:text-white mb-4"
            >
              <FaArrowLeft />
              <span>Back to Dashboard</span>
            </button>
            <h1 className="text-4xl font-bold">System Settings</h1>
            <p className="text-gray-400 mt-2">Configure platform behavior and features</p>
          </div>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.includes('Failed') 
              ? 'bg-red-500/20 border border-red-500 text-red-400' 
              : 'bg-green-500/20 border border-green-500 text-green-400'
          }`}>
            {message}
          </div>
        )}

        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold mb-4">User Management</h2>
            {renderBooleanSetting(
              'user_registration_enabled',
              'User Registration',
              'Allow new users to register accounts on the platform'
            )}
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4">Platform Configuration</h2>
            {renderTextSetting(
              'platform_name',
              'Platform Name',
              'The name displayed to users throughout the platform'
            )}
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4">System Maintenance</h2>
            {renderBooleanSetting(
              'maintenance_mode',
              'Maintenance Mode',
              'Enable maintenance mode to temporarily disable platform access'
            )}
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4">Real-time Updates</h2>
            {renderNumberSetting(
              'donation_polling_interval',
              'Donation Polling Interval',
              'How often the donation page checks for new donations (set to 0 to disable)',
              'seconds',
              0,
              300
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminSettings;